import gql from 'graphql-tag';
import { useQuery, useResult, useMutation } from '@vue/apollo-composable';
import { cloneDeep } from 'lodash-es';
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { usePermission } from '@models/profile/useProfilePermission';
import { useGuideStore } from '@/pages/master/store/guide.store';
import {
    clearExperienceList,
    deleteExperienceFromList,
    updateExperienceFromList
} from '@/models/experience/useExperienceList';
import {
    formatExperienceInfo,
    checkAndUpdateExperienceVersionFormFields,
    judgeHasSync,
    createIntegrationShopifyFields
} from '@experience/hooks/setting.hooks';
import { zip } from '@utils/string.utils';
import {
    REDIRECT_PAGE_CONDITION_KEY,
    TYPE_ANY_PARAMETER,
    TYPE_SPECIFIC_PARAMETER
} from '@experience/static/config/experience.config';

export type ExperienceStatusType = 'PAUSE' | 'SCHEDULED' | 'DRAFT' | 'RUNNING' | 'UNSCHEDULED';
export type TrafficModeType = 'AVERAGE' | 'CUSTOM' | 'BANDIT';
export enum DeviceType {
    MOBILE = 1,
    PC = 2
}

export enum TerminalStatus {
    OFF = 0,
    ON = 1
}

export type ExperienceType =
    | 'POPUP'
    | 'STICKY_BAR'
    | 'INLINE'
    | 'ADVANCED'
    | 'REDIRECT'
    | 'DEFAULT';

export interface Usergroup {
    type: 'USER_GROUP' | 'CONDITION';
    value: any;
}

export interface Redirect {
    condition: string;
    value: string;
    toValue: string;
    previewUrl: string;
    advanceEnable?: boolean;
}

export interface ExperienceVersion {
    id: string;
    name: string;
    rate: number;
    controlGroup: boolean;
    screenshot: string;
    engageID: string;
    terminals: ExperienceCreativeTerminal[];
}

export interface ExperienceCreativeTerminal {
    id: string;
    type: DeviceType;
    status: TerminalStatus;
    popup: any;
    stickybar: any;
    code: any;
    inline: any;
    engageVersionID: string;
    redirect: Redirect;
}

export interface Goal {
    id: string;
    sid: string;
    name: string;
    content: any;
    description: string;
    createTime: number;
    updateTime: number;
    createBy: string;
    modifierBy: string;
    status: number;
    count: number;
}

export interface Automation {
    pushBusinessType: 'WHEN_SUBMIT_FORM';
    actions: {
        platform: 'SHOPIFY';
        config: {
            connectionName: string;
            connectionID: string;
            fieldMapping: {
                ptengineErrorInfo: string;
                shopifyErrorInfo: string;
                id: string[];
                ptengine: string;
                shopify: string;
            }[];
        };
    }[];
}
export interface Experience {
    id: string;
    sid: string;
    name: string;
    status: ExperienceStatusType;
    createTime: number;
    updateTime: number;
    lastPauseTime: number;
    firstRunTime: number;
    hasSync: boolean;
    hasForm: boolean;
    isVersionEvent: boolean;
    trafficMode: TrafficModeType;
    createBy: string;
    modifierBy: string;
    enableABTest: boolean;
    hasVariant: boolean;
    calculateConfidence?: number;
    calculateDays?: number;
    engageVersion: string;
    engageType: ExperienceType;
    baseURL: string;
    screenshot: string;
    automations: Automation[];
    schedule: any;
    conditions: any;
    usergroups: Usergroup[];
    versions: ExperienceVersion[];
    goals: Goal[];
    count: number;
    trigger: any;
    templateSource?: {
        id?: string;
        name?: string;
        type?: string;
        purpos?: string[];
        industry?: string[];
    };
    antiFlicker?: boolean;
    banditGoal?: string | null;
}

export type CreateExperienceV4Input = {
    id: string;
    name: string;
    engageType?: ExperienceType;
    isABTest: boolean;
    baseUrl?: string;
    schedule: any;
    conditions: any;
    baseURL: String;
    usergroups?: Usergroup[];
    trigger: any;
    versions?: ExperienceVersion[];
    templateSource?: {
        id?: string;
        name?: string;
        type?: string;
        purpos?: string[];
        industry?: string[];
    };
};

export type UpdateEngageV4Input = {
    name?: string;
    status?: ExperienceStatusType;
    trafficMode?: TrafficModeType;
    hasSync?: boolean;
    hasVariant?: boolean;
    engageType?: ExperienceType;
    hasForm?: boolean;
    isVersionEvent?: boolean;
    baseURL?: string;
    schedule?: any;
    automations?: Automation[];
    conditions?: any;
    usergroups?: Usergroup[];
    versions?: ExperienceVersion[];
    goals?: Goal[];
    enableABTest?: boolean;
    calculateConfidence?: number;
    calculateDays?: number;
    antiFlicker?: boolean;
    banditGoal?: string | null;
};

export interface StartType {
    radio: string;
    UserBlur: boolean;
    UserStay: boolean;
    ScrollMatch: boolean;
    done: boolean;
    UserStayValue: string;
    ScrollMatchValue: string;
    ShownByJs: boolean;
    UserStopValue: string;
    UserStop: boolean;
}
export interface StopType {
    radio: string;
    SeenTimes: boolean;
    SeenTimesValue: 5;
    CloseTimes: boolean;
    CloseTimesValue: string;
    ClickButton: boolean;
    FormSubmitted: boolean;
    done: boolean;
}
export interface FrequencyType {
    type: 'default' | 'session' | 'day';
    session: {
        times: number;
    };
    day: {
        times: number;
        days: number;
    };
}

export interface TriggerType {
    start: [];
    stop: [];
    frequency: {};
}
export const EngageV4Type = `{
    id
    sid
    name
    status
    createTime
    updateTime
    lastPauseTime
    firstRunTime
    hasSync
    trafficMode
    hasForm
    isVersionEvent
    createBy
    modifierBy
    hasVariant
    calculateConfidence
    calculateDays
    engageVersion
    engageType
    enableABTest
    baseURL
    screenshot
    schedule
    automations {
        pushBusinessType
        actions {
            platform
            config
            errorCode
        }
    }
    conditions
    trigger
    goals {
        id
        name
    }
    usergroups {
        type
        value
    }
    versions {
        id
        name
        rate
        controlGroup
        screenshot
        terminals {
            id
            type
            popup
            stickybar
            inline
            code
            status
            redirect
        }
    }
    count
    templateSource
    antiFlicker
    banditGoal
}
`;

const EngageV4TypePublish = `{
    engageType
    enableABTest
    templateSource
}`;

export const EXPERIENCE_QUERY = gql`
    query getEngageV4($id: ID!) {
        getEngageV4(id: $id) ${EngageV4Type}
    }
`;

const CREATE_EXPERIENCE_MUTATION = gql`
    mutation createEngageV4($input: CreateEngageV4Input!) {
        createEngageV4(input: $input) ${EngageV4Type}
    }
`;

const UPDATE_EXPERIENCE_MUTATION = gql`
    mutation updateEngageV4($gzipBase64Input: String!) {
        updateEngageV4(gzipBase64Input: $gzipBase64Input)
    }
`;
// graphql - experience暂停
const PAUSE_EXPERIENCE_MUTATION = gql`
    mutation pauseEngageV4($id: ID!) {
        pauseEngageV4(id: $id)
    }
`;
// graphql - experience发布
const PUBLISH_EXPERIENCE_MUTATION = gql`
    mutation publishEngageV4($id: ID!) {
        publishEngageV4(id: $id) ${EngageV4TypePublish}
    }
`;
// graphql - experience更新
const SCHEDULE_EXPERIENCE_MUTATION = gql`
    mutation saveAndScheduleEngageV4($id: ID!) {
        saveAndScheduleEngageV4(id: $id)
    }
`;
// graphql - experience删除
const DELETE_EXPERIENCE_MUTATION = gql`
    mutation deleteEngageV4($id: ID!) {
        deleteEngageV4(id: $id) ${EngageV4Type}
    }
`;

// graphql - experience复制
const COPY_EXPERIENCE_MUTATION = gql`
    mutation copyEngageV4($id: ID!) {
        copyEngageV4(id: $id) ${EngageV4Type}
    }
`;

const { getProfilePackageInfo } = usePackageInfo();
const { getProfileNpPackageInfo } = useNPPackageInfo();

const experienceInfo = ref<Experience | null>();
const experienceLoading = ref(false);
const experienceInfoResult = ref<Experience | null>(); // format前的值

export function useExperience() {
    const route = useRoute();
    const router = useRouter();
    const { hasIntegrationShopifyPermission } = usePermission();
    const { getStepConfig, updateStepsStatus } = useGuideStore();
    const experienceId = computed(() => {
        return route.params?.experienceId;
    });

    const experienceOtherInfo = computed(() => {
        if (!experienceInfo.value) {
            return null;
        }
        const { engageType, hasForm, enableABTest, status, trafficMode } = experienceInfo.value;
        const { usergroups } = experienceInfoResult.value || {};
        const hasPopup = getPopup(experienceInfo);
        const hasAutomation = getAutomation(experienceInfo.value);
        const redirectParamsType = getRedirectParamsType(experienceInfo.value);
        return {
            redirectParamsType,
            engageType,
            hasPopup,
            hasAutomation: hasAutomation,
            hasForm: hasForm,
            hasAbtest: enableABTest,
            isRedirectTest: engageType?.toLocaleUpperCase() === 'REDIRECT',
            visibleTrigger: ['POPUP', 'STICKY_BAR'].includes(
                engageType?.toLocaleUpperCase() as string
            ),
            visiblePageCondition: (engageType?.toLocaleUpperCase() as string) !== 'REDIRECT',
            isRunning: status === 'RUNNING',
            isDraft: status === 'DRAFT',
            isRunningOrScheduled: ['RUNNING', 'SCHEDULED'].includes(status),
            isCustomTrafficMode: trafficMode === 'CUSTOM',
            isBanditTrafficMode: trafficMode === 'BANDIT',
            isInlineOrAdvance: ['INLINE', 'ADVANCED'].includes(
                engageType?.toLocaleUpperCase() as string
            ),
            isPopupOrStickybar: ['POPUP', 'STICKY_BAR'].includes(
                engageType?.toLocaleUpperCase() as string
            ),
            isUnknowType: engageType?.toLocaleUpperCase() === 'DEFAULT',
            isAllPeople:
                !usergroups ||
                usergroups.length <= 0 ||
                usergroups.some(
                    item =>
                        item.type === 'USER_GROUP' && item.value.some(i => i.source === 'default')
                )
        };
    });

    function getAutomation(experienceInfo: Experience) {
        // automations 不为空且存在表单字段就展示入口，否则隐藏
        const automations = experienceInfo?.automations ?? [];
        const result = createIntegrationShopifyFields(experienceInfo, false);
        return (
            hasIntegrationShopifyPermission.value &&
            (automations.length !== 0 || result.length !== 0)
        );
    }

    function getRedirectParamsType(experienceInfo: Experience): 'CombinedPage' | 'URL' | null {
        if (experienceInfo.engageType !== 'REDIRECT') {
            return null;
        }
        const when = experienceInfo.conditions?.[0]?.when ?? [];
        if (!when.length) {
            return TYPE_ANY_PARAMETER;
        }
        // 看当前的when去掉type为CombinedPage后是否是否还有其他项，有则会回显示第二个，否则回显第一个
        const otherConditions = when.filter(item => item.uuid !== REDIRECT_PAGE_CONDITION_KEY);
        const isExistConditions = !!otherConditions.length;
        // URL带参数，带条件
        if (isExistConditions) {
            return TYPE_SPECIFIC_PARAMETER;
        }
        return TYPE_ANY_PARAMETER;
    }

    function getPopup(data: any) {
        if (['POPUP', 'STICKY_BAR'].includes(data.value?.engageType?.toLocaleUpperCase())) {
            return true;
        } else if (data.value?.engageType === 'INLINE') {
            return false;
        } else if (data.value?.engageType === 'ADVANCED') {
            return data.value.versions?.some((version: any) => {
                return version.terminals?.some((terminal: any) => {
                    return (
                        (terminal.popup && terminal.popup.length > 0) ||
                        (terminal.stickybar && terminal.stickybar.length > 0)
                    );
                });
            });
        }
        return null;
    }
    // 获取 Experience
    function getExperience(
        experienceIdParam?: string,
        resultCallback?: (T: Experience | null) => void,
        rejectCallback?: (err: any) => void
    ) {
        const id = experienceId.value || experienceIdParam;
        if (id) {
            experienceLoading.value = true;

            const { result, onError, onResult } = useQuery<{ getEngageV4: Experience }>(
                EXPERIENCE_QUERY,
                () => {
                    return {
                        id
                    };
                },
                {
                    fetchPolicy: 'no-cache' //cache-and-network cache-first
                }
            );
            onResult(data => {
                if (data?.data?.getEngageV4) {
                    checkAndUpdateExperienceVersionFormFields(data.data.getEngageV4);
                    experienceInfoResult.value = data.data.getEngageV4;
                    experienceInfo.value = formatExperienceInfo(data.data.getEngageV4);
                    // 执行回调
                    resultCallback &&
                        typeof resultCallback === 'function' &&
                        resultCallback(experienceInfo.value);
                } else if (rejectCallback && typeof rejectCallback === 'function') {
                    rejectCallback('get experience failed.');
                } else {
                    // 获取失败，且没有错误回调函数，则默认跳转至列表页
                    router.replace({
                        name: 'ExperienceList'
                    });
                }
                experienceLoading.value = false;
            });
            onError(err => {
                experienceLoading.value = false;
                if (rejectCallback && typeof rejectCallback === 'function') {
                    rejectCallback('get experience failed.');
                } else {
                    // 获取失败，且没有错误回调函数，则默认跳转至列表页
                    router.replace({
                        name: 'ExperienceList'
                    });
                }
            });
        }
    }

    // 创建 Experience
    const {
        mutate: createExperienceMutate,
        loading: createExperienceLoading,
        error: createExperienceError,
        onError: onCreateExperienceError
    } = useMutation(CREATE_EXPERIENCE_MUTATION, {
        errorPolicy: 'all'
    });
    function createExperience(
        param: CreateExperienceV4Input,
        onSuccess?: (res?: any) => void,
        onFailed?: (err?: any) => void
    ) {
        createExperienceMutate({ input: param }).then(
            result => {
                getProfilePackageInfo();
                getProfileNpPackageInfo();
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.createEngageV4);
                // clearExperienceList(); //清除列表信息，进入列表页是重新获取
            },
            err => {
                onFailed && typeof onFailed === 'function' && onFailed(err);
            }
        );
    }

    // 更新且更新Experience
    const {
        mutate: updateExperienceMutate,
        loading: updateExperienceMutateLoading,
        error: updateExperienceError
    } = useMutation(UPDATE_EXPERIENCE_MUTATION);
    const updateExperience = (
        param: UpdateEngageV4Input,
        onSuccess?: (res?: any) => void,
        onError?: (err?: string) => void
    ) => {
        // running或schedule状态下，判断内容有没有更新，如果有更新hasSync设置为false
        if (experienceOtherInfo.value?.isRunningOrScheduled) {
            const hasUpdate =
                experienceInfoResult.value && judgeHasSync(param, experienceInfoResult.value);
            hasUpdate && (param.hasSync = false);
        }
        // 更新编辑器的时候会附带hasForm参数，判断是否更新了表单
        if (param.hasForm !== undefined && !param.hasForm) {
            delete param.hasForm;
        }
        if (experienceInfo.value && param.versions) {
            const newExperienceInfo: Experience = {
                ...experienceInfo.value,
                versions: param.versions
            };
            const automations = checkAndUpdateExperienceVersionFormFields(newExperienceInfo);
            // 判断automations是否存在变更，如果存在则带着表单表单变化一起上传，否则只更新编辑器的表单
            Object.assign(param, automations ? { automations } : {});
        }
        const inputData = {
            id: route.params?.experienceId || experienceId.value,
            ...param
        };
        const binaryString = zip(JSON.stringify(inputData));
        updateExperienceMutate({ gzipBase64Input: binaryString }).then(
            res => {
                if (experienceInfo.value) {
                    for (const key in param) {
                        type KeyType = keyof UpdateEngageV4Input;
                        const initialValue = param[key as KeyType];
                        (experienceInfo.value[key as KeyType] as any) = formatExperienceInfo(
                            cloneDeep(initialValue)
                        );
                    }
                }
                onSuccess && typeof onSuccess === 'function' && onSuccess(experienceInfo.value);
                updateExperienceFromList(inputData.id as string, param);
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    // 删除 Experience
    const {
        mutate: deleteExperienceMutate,
        loading: deleteExperienceLoading,
        onError: deleteExperienceError
    } = useMutation(DELETE_EXPERIENCE_MUTATION);
    const deleteExperience = (
        id: string,
        onSuccess?: (res?: any) => void,
        onError?: (res?: any) => void
    ) => {
        return deleteExperienceMutate({ id }).then(
            (result: any) => {
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.deleteEngageV4);
                getProfilePackageInfo();
                getProfileNpPackageInfo();
                result?.data?.deleteEngageV4 && deleteExperienceFromList(id);

                if (result?.data?.deleteEngageV4?.count === 0) {
                    setTimeout(function () {
                        router.replace({
                            name: 'ExperienceListGuide'
                        });
                    });
                }
                return result.data.deleteEngageV4;
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    // 暂停experience
    const {
        mutate: pauseExperienceMutate,
        loading: pauseExperienceLoading,
        onError: pauseExperienceError
    } = useMutation(PAUSE_EXPERIENCE_MUTATION);
    const pauseExperience = (
        id: string,
        onSuccess?: (res?: any) => void,
        onError?: (res?: any) => void
    ) => {
        return pauseExperienceMutate({ id }).then(
            (result: any) => {
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.pauseEngageV4);
                result?.data?.pauseEngageV4 && updateExperienceFromList(id, { status: 'PAUSE' });
                return result.data.pauseEngageV4;
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    // schedule experience
    const {
        mutate: scheduleExperienceMutate,
        loading: scheduleExperienceLoading,
        onError: onScheduleExperienceError,
        onDone: onScheduleExperienceDone
    } = useMutation(SCHEDULE_EXPERIENCE_MUTATION);
    const scheduleExperience = (
        id: string,
        status: 'UNSCHEDULED' | 'SCHEDULED',
        onSuccess?: (res?: any) => void,
        onError?: (res?: any) => void
    ) => {
        scheduleExperienceMutate({ id }).then(
            result => {
                if (result?.data?.saveAndScheduleEngageV4) {
                    onSuccess &&
                        typeof onSuccess === 'function' &&
                        onSuccess(result?.data?.saveAndScheduleEngageV4);

                    if (result?.data?.saveAndScheduleEngageV4) {
                        updateExperienceFromList(id, { status });
                        // 更新userGuide 完成状态
                        if (!getStepConfig('startGuide_experiencePublish')?.done) {
                            updateStepsStatus(['startGuide_experiencePublish']);
                        }
                    }
                } else {
                    onError && typeof onError === 'function' && onError();
                }
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    // 发布experience
    const {
        mutate: publishExperienceMutate,
        loading: publishExperienceLoading,
        onError: publishExperienceError
    } = useMutation(PUBLISH_EXPERIENCE_MUTATION);
    const publishExperience = (
        id: string,
        onSuccess?: (res?: any) => void,
        onError?: (res?: any) => void
    ) => {
        return publishExperienceMutate({ id }).then(
            (result: any) => {
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.publishEngageV4);
                if (result?.data?.publishEngageV4) {
                    updateExperienceFromList(id, { status: 'RUNNING' });
                    // 更新userGuide 完成状态
                    if (!getStepConfig('startGuide_experiencePublish')?.done) {
                        updateStepsStatus(['startGuide_experiencePublish']);
                    }
                }

                return result.data.publishEngageV4;
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    // 复制
    const {
        mutate: copyExperienceMutate,
        loading: copyExperienceLoading,
        onError: copyExperienceError
    } = useMutation(COPY_EXPERIENCE_MUTATION);
    const copyExperience = (
        id: string,
        onSuccess?: (res?: any) => void,
        onError?: (res?: any) => void
    ) => {
        return copyExperienceMutate({ id }).then(
            (result: any) => {
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.copyEngageV4);
                getProfilePackageInfo();
                getProfileNpPackageInfo();
                // clearExperienceList(); //清除列表信息，进入列表页是重新获取
                return result.data.copyEngageV4;
            },
            err => {
                onError && typeof onError === 'function' && onError(err);
            }
        );
    };

    /**
     * 更新userGuide的experience部分的进度状态
     */
    const updateExperienceUserGuideStatus = (experience: Experience) => {
        if (!experience) {
            return;
        }
        const { engageType, versions } = experience;

        const needUpdateItem: GuideKey[] = [];
        const needUpdateABtestStatus =
            versions?.length >= 2 && !getStepConfig('startGuide_abTest')?.done;
        needUpdateABtestStatus && needUpdateItem.push('startGuide_abTest');
        if (
            ['STICKY_BAR', 'POPUP'].includes(engageType) &&
            !getStepConfig('startGuide_popup')?.done
        ) {
            needUpdateItem.push('startGuide_popup');
        }

        if (engageType === 'INLINE' && !getStepConfig('startGuide_inline')?.done) {
            needUpdateItem.push('startGuide_inline');
        }
        needUpdateItem.length > 0 && updateStepsStatus(needUpdateItem);
    };

    return {
        experienceInfoResult,
        experienceInfo,
        experienceOtherInfo,
        experienceLoading,
        createExperienceLoading,
        deleteExperienceLoading,
        pauseExperienceLoading,
        updateExperienceMutateLoading,
        scheduleExperienceLoading,
        publishExperienceLoading,
        copyExperienceLoading,
        updateExperienceError,

        getExperience,
        createExperience,
        updateExperience,
        pauseExperience,
        publishExperience,
        scheduleExperience,
        onScheduleExperienceDone,
        onScheduleExperienceError,
        deleteExperience,
        copyExperience,
        updateExperienceUserGuideStatus
    };
}
